interface CachedData<T> {
  data: T;
  timestamp: number;
}

export const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export const CACHE_KEYS = {
  FEATURED_ARTICLES: 'featuredArticles',
  ARTICLE_BY_SLUG: (slug: string) => `article_${slug}`,
  ARTICLES_BY_CATEGORY: (categorySlug: string) => `category_${categorySlug}`,
  NETWORK_STATUS: 'networkStatus',
  PENDING_SUBSCRIBERS: 'pendingSubscribers',
  USER: 'currentUser', 
  AUTH_TOKEN: 'authToken' 
};

/**
 * Saves data to the browser's localStorage with timestamp
 * @param key The cache key
 * @param data The data to cache
 * @returns Boolean indicating success
 */
export const saveToCache = <T>(key: string, data: T, expirationMinutes?: number): void => {
  try {
    const item = {
      data,
      timestamp: Date.now(),
      expiration: expirationMinutes ? Date.now() + expirationMinutes * 60 * 1000 : null
    };
    localStorage.setItem(key, JSON.stringify(item));
  } catch (error) {
    console.error('Error saving to cache:', error);
  }
};

/**
 * Retrieves data from cache if it's still valid (not expired)
 * @param key The cache key
 * @returns The cached data or null if not found/expired
 */
export const getFromCache = <T>(key: string): T | null => {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;
    
    const parsedItem = JSON.parse(item);
    
    // Check if the item has expired
    if (parsedItem.expiration && Date.now() > parsedItem.expiration) {
      localStorage.removeItem(key);
      return null;
    }
    
    return parsedItem.data as T;
  } catch (error) {
    console.error('Error getting from cache:', error);
    return null;
  }
};

/**
 * Removes an item from the cache
 * @param key The cache key
 */
export const removeFromCache = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing from cache:', error);
  }
};

/**
 * Clears all cached data
 */
export const clearCache = (): void => {
  try {
    localStorage.clear();
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};